import { defineComponent as _defineComponent } from 'vue'
import bugsnagClient from 'Src/js/global/bugsnag';
import globalData from '../../global/window-data';
import Icon from 'JS/components/Icon.vue';
import searchAutocompleteService, { SearchAutocompleteResult } from 'JS/header/services/SearchAutocompleteService';
import SearchSuggestions from './SearchSuggestions.vue';
import vClickOutside from '../../directives/click-outside';
import vTrapFocus from '../../directives/trap-focus';
import { computed, onMounted, ref, watch } from 'vue';
import { debounce } from 'JS/utilities/debounce';
import { v4 as genUUID } from 'uuid';
import { IHawkSearchTrackingData } from 'Src/js/types/HawkSearch';


export default /*#__PURE__*/_defineComponent({
  __name: 'SearchBar',
  setup(__props) {

const searchInputId = genUUID();
const ariaMessageId = genUUID();

const form = ref<HTMLFormElement | null>(null);
const suggestionsEl = ref<InstanceType<typeof SearchSuggestions> | null>(null);
const input = ref<HTMLInputElement | null>(null);
const keyword = ref(getInitialKeyword());
const triggerAriaMessage = ref(false);
const suggestions = ref<SearchAutocompleteResult | null>(null);
const suggestionIndex = ref<number>(0);
const testGroup = ref(0);
onMounted(() => {
	// globalData can only be access on the client
	testGroup.value = globalData.testGroup;
});

const searchBarState = computed((): string => {
	return suggestions.value ? "Suggestions Expanded Press Escape to Close" : "Suggestions Collapsed";
});

function getInitialKeyword(): string {
	// header is pre-rendered, so avoid attempting window access in node
	if (typeof window === 'undefined') {
		return '';
	}

	// prevent search keyword from being populated on pages other than the main search.
	// avoids a potentially confusing experience where changing the search keyword on some
	// page other than the main search would take a user out of the current search context
	// (e.g. sales flyer search) and to the main search page
	const isMainSearchPage = window.location.pathname.startsWith('/s/');
	if (!isMainSearchPage) {
		return '';
	}

	const searchKeyword = new URLSearchParams(window.location.search).get('keyword');
	return searchKeyword ?? '';
}

function submitValidation(event: SubmitEvent) {
	if (!keyword.value) {
		triggerAriaMessage.value = true;
		event.preventDefault();
	}

	// Temporary fix for comma
	if (keyword.value.indexOf(",") !== -1) keyword.value = keyword.value.replace(",", "");

	trackSearch({
		keyword: keyword.value.trim()
	});
};

function handleKeyDown(ev: KeyboardEvent) {
	if (!suggestions.value) {
		if (ev.key === 'ArrowDown') {
			doAutocomplete();
		}

		return;
	};

	switch (ev.key) {
		case 'Escape':
			input.value?.focus();
			closeSuggestions();
			break;
		case 'ArrowDown':
			ev.preventDefault();
			suggestionIndex.value++;
			focusCategorySuggestion();
			break;
		case 'ArrowUp':
			ev.preventDefault();
			suggestionIndex.value--;
			focusCategorySuggestion();
			break;
		default:
			break;
	}
}

function handleKeyUp(ev: KeyboardEvent) {
	switch (ev.key) {
		case 'Enter':
			closeSuggestions();
		default:
			break;
	}
}

function closeSuggestions() {
	if (!suggestions.value) {
		return;
	}
	suggestions.value = null;
	suggestionIndex.value = 0;
}

function focusCategorySuggestion() {
	const el = suggestionsEl.value?.$el;
	if (!el) {
		return;
	}

	const suggestions = el.querySelectorAll('a').length;
	if (!el.contains(document.activeElement)) {
		suggestionIndex.value = 0;
	} else if (suggestionIndex.value < 0) {
		suggestionIndex.value = suggestions - 1;
	} else if (suggestionIndex.value + 1 > suggestions) {
		suggestionIndex.value = 0;
	}

	el.querySelectorAll('a')[suggestionIndex.value]?.focus();
}

function handleSearchInputFocus() {
	if (!suggestions.value) {
		doAutocomplete();
	}
}

function handleSearchInputBlur() {
	// prevent new suggestions from being populated if there's an autocomplete request in flight
	abortController.abort();
}

const handleKeywordChangeDebounced = debounce(250, ()=> {
	doAutocomplete();
	if (keyword.value) {
		triggerAriaMessage.value = false;
	}
});

watch(keyword, () => {
	handleKeywordChangeDebounced();
});

let cachedInstantEngageRes: Promise<SearchAutocompleteResult> | null;
let cachedLastSuggestionRes: {
	keyword: string;
	res: Promise<SearchAutocompleteResult>;
} | null;
async function getAutocompleteResult(keyword: string) {
	if (!keyword.trim()) {
		cachedInstantEngageRes ??= searchAutocompleteService.autocomplete();
		return await cachedInstantEngageRes;
	}

	try {
		if (cachedLastSuggestionRes?.keyword !== keyword) {
			// IDEA: set cache headers on response from backend instead
			const res = searchAutocompleteService.autocomplete(keyword);

			cachedLastSuggestionRes = {
				keyword,
				res,
			};
		}


		return await cachedLastSuggestionRes.res;
	} catch (e) {
		cachedLastSuggestionRes = null;
	}

}

let abortController = new AbortController();
let popularSearchABTestTimeout: number;
// TODO: Consider debouncing this
async function doAutocomplete() {
	abortController.abort();
	const currentReqAbortController = new AbortController();

	abortController = currentReqAbortController;

	// BEGIN: autosuggest popular search A/B test
	clearTimeout(popularSearchABTestTimeout);
	// END: autosuggest popular search A/B test

	if (/*!keyword.value || keyword.value.trim().length < 2 || */keyword.value.trim().match(/^[0-9]{1,4}$/g)) {
		closeSuggestions();
		return;
	}

	try {
		// TODO: would this not re-trigger the watch?
		if (keyword.value.length > 150) {
			keyword.value = keyword.value.slice(0, 149);
		}

		const response = await getAutocompleteResult(keyword.value);

		if (currentReqAbortController.signal.aborted) {
			return;
		}

		const autoCompBrands = response?.brands ?? [];
		const autoCompCategories = response?.categories ?? [];
		const hasFacetSuggestions = autoCompBrands.length > 0 || autoCompCategories.length > 0;
		if (!response?.products?.length && !hasFacetSuggestions) {
			suggestions.value = null;
			return;
		}

		suggestions.value = response;
		suggestionIndex.value = 0;

		// BEGIN: autosuggest popular search A/B test
		if (response.popularSearches.length > 0) {
			popularSearchABTestTimeout = window.setTimeout(() => {
				window.blainGtmDataLayer.push({
					'event': "search_autosuggest"
				});
			}, 1500);
		}
		// END: autosuggest popular search A/B test
	} catch (e) {
		if (!e) return; // Don't message-less errors for failed network requests to hawk
		bugsnagClient.notify(e);
	}
}

function trackSearch(event: IHawkSearchTrackingData) {
	window.blainGtmDataLayer.push({
		'searchKeyword': event.keyword,
		'event': 'search_by_keyword'
	});
}

return { __sfc: true,searchInputId, ariaMessageId, form, suggestionsEl, input, keyword, triggerAriaMessage, suggestions, suggestionIndex, testGroup, searchBarState, getInitialKeyword, submitValidation, handleKeyDown, handleKeyUp, closeSuggestions, focusCategorySuggestion, handleSearchInputFocus, handleSearchInputBlur, handleKeywordChangeDebounced, cachedInstantEngageRes, cachedLastSuggestionRes, getAutocompleteResult, abortController, popularSearchABTestTimeout, doAutocomplete, trackSearch, Icon, SearchSuggestions, vClickOutside, vTrapFocus }
}

})